import { Box, VStack } from '@chakra-ui/react';
import {
  Message,
  ConversationHandover,
} from '@/features/messages/messages.types';
import { MessageItem } from './conversation-message-item';
import { ThreadDivider } from './conversation-thread-divider';
import {
  DateGroup,
  ConversationUser,
  ConversationAgent,
} from '../conversations.types';
import { getSenderDetails } from './conversation-sender-utils';
import { formatDateHeader } from './conversation-message-utils';

interface DateGroupSectionProps {
  group: DateGroup;
  user: ConversationUser | null;
  aiAgent: ConversationAgent;
  humanAgent: ConversationAgent;
}

export const DateGroupSection = ({
  group,
  user,
  aiAgent,
  humanAgent,
}: DateGroupSectionProps) => {
  return (
    <Box mb={6}>
      <ThreadDivider text={formatDateHeader(group.date)} type="time" />

      <VStack gap={4} align="stretch">
        {group.items.map((item) => {
          // Handle handover items
          if (
            item.content &&
            'type' in item.content &&
            item.content.type === 'handover'
          ) {
            const handover = item.content as ConversationHandover;
            const isToAgent = handover.is_agent_handover;
            const handoverText = isToAgent
              ? `Conversation handed to AI assistant${handover.agent_id ? ` (${handover.agent_id})` : ''}`
              : `Conversation taken over by ${handover.to_user_id ? 'agent' : 'human agent'}`;

            return (
              <ThreadDivider
                key={`handover-${handover.id}`}
                text={handover.handover_reason || handoverText}
                type={isToAgent ? 'ai-takeover' : 'takeover'}
              />
            );
          }

          // Handle system messages
          const message = item.content as Message;

          if (message.is_system_message) {
            const isAIHandover = message.content.includes('handed back');
            return (
              <ThreadDivider
                key={`system-${message.id}`}
                text={message.content}
                type={isAIHandover ? 'ai-takeover' : 'takeover'}
              />
            );
          }

          // Regular user/agent messages
          const senderDetails = getSenderDetails(
            message,
            user,
            aiAgent,
            humanAgent,
          );

          return (
            <MessageItem
              key={`msg-${message.id}`}
              message={message}
              isUser={senderDetails.isUser}
              senderAvatar={senderDetails.avatar}
              senderName={senderDetails.name}
              isAIAgent={senderDetails.isAIAgent}
              messageStatus={item._status || 'delivered'}
            />
          );
        })}
      </VStack>
    </Box>
  );
};
